import { getProductTypeByTag } from "@component/Widget/ArticleEndForm/ArticleEndForm.fn";
import { Config } from "@config";
import { config } from "@fortawesome/fontawesome-svg-core";
import { Console } from "@utils/console";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { DOMContentLoaded } from "@utils/loader";
import { sentryInit } from "@utils/sentry-error";
import { webviewFixer } from "@utils/webview";
import { http, Cookie, getUrlParameter } from "bm-ui-kit";
import React from "react";
import reactDom from "react-dom";

config.autoAddCss = false;

GoogleAnalytics4.init();

type ShortcodeGroups = { shortcode: string; elements: Element[] }[];

export const staticShortCodes = ["ad", "search_form", "newsletter_subscribe", "footer_menu", "main_menu"];

export function collectReactComponents(): ShortcodeGroups {
	const elementsByComponents: Record<string, Element[]> = {};

	Array.from(document.getElementsByClassName("react-component")).forEach((element) => {
		const name = element.getAttribute("data-component");

		if (!elementsByComponents[name]) {
			elementsByComponents[name] = [];
		}

		elementsByComponents[name].push(element);
	});

	return Object.entries(elementsByComponents).map(([shortcode, elements]) => ({ shortcode, elements }));
}

function requestIdle(): Promise<void> {
	return new Promise((resolve) => {
		if ("requestIdleCallback" in window) {
			window.requestIdleCallback(
				() => {
					resolve();
				},
				{
					timeout: 5_000,
				},
			);
		} else {
			setTimeout(
				() => {
					resolve();
				},
				1_000 + 1_000 * Math.random(),
			);
		}
	});
}

export function init(): Promise<ShortcodeGroups> {
	if (Config.getSentryDsn()) {
		sentryInit(Config.getSentryDsn(), `bankmonitor-hu-webapp@${BM_VERSION}`, Config.getEnvironment());
	}

	const debug = getUrlParameter("debug");

	if (
		debug !== undefined &&
		(Config.getEnvironment() !== "live" || Config.getCurrentUser().getEmail().includes("@bankmonitor.hu"))
	) {
		Cookie.set("bm-debug", debug);
	}

	return new Promise((resolve) => {
		DOMContentLoaded(() => {
			http.setDefaultHeader({
				"bm-application": "wordpress-js",
				"bm-referer": window.location.href,
				"X-WP-Nonce": Config.getNonce(),
			});

			resolve(collectReactComponents());

			webviewFixer();
		});
	});
}

export function loadReactComponents(
	groups: ShortcodeGroups,
	loadComponent: (shortcode: string, elements: Element[]) => void,
): void {
	groups.forEach(({ shortcode, elements }) => {
		const lazyHydrate = elements[0]?.getAttribute("data-lazy-hydrate");

		elements.forEach((element) => {
			const dataProps = element.getAttribute("data-props");
			const propsJson =
				(dataProps &&
					JSON.parse(dataProps, (_key, value) => {
						if (value === "true") {
							return true;
						}

						if (value === "false") {
							return false;
						}

						if (typeof value === "string" && value.startsWith("{")) {
							return JSON.parse(value.replace(/&quot;/g, '"'));
						}

						return value;
					})) ||
				{};
			const dataEvent = element.getAttribute("data-event");
			const eventJson = dataEvent && JSON.parse(dataEvent);

			if (eventJson?.componentType) {
				const ga4Event = {
					...(propsJson?.ga4Events || {}),
					...eventJson,
					instanceId: lazyHydrate ? GoogleAnalytics4.generateId("id") : undefined,
				};

				if (ga4Event?.componentName === "ArticleEndForm" && propsJson?.tag) {
					ga4Event.productTypes = getProductTypeByTag(propsJson.tag);
				}

				propsJson.ga4Event = ga4Event;
			}
			element.setAttribute("data-props", JSON.stringify(propsJson));
			element.removeAttribute("data-event");

			if (lazyHydrate && propsJson?.ga4Event?.componentType) {
				GoogleAnalytics4.sendEvent("BM_componentCreate", propsJson.ga4Event);
			}
		});

		switch (lazyHydrate) {
			case "whenIdle":
				requestIdle().then(() => {
					loadComponent(shortcode, elements);
				});
				break;
			case "whenVisible":
				requestIdle().then(() => {
					if ("IntersectionObserver" in window) {
						const shortcodeObserver = new IntersectionObserver((entries) => {
							entries.forEach((entry) => {
								if (entry.isIntersecting) {
									loadComponent(shortcode, elements);

									elements.forEach((element) => {
										shortcodeObserver.unobserve(element);
									});
								}
							});
						});

						elements.forEach((element) => {
							shortcodeObserver.observe(element);
						});
					} else {
						loadComponent(shortcode, elements);
					}
				});
				break;
			default:
				loadComponent(shortcode, elements);
		}
	});
}

export function renderComponent(
	shortcode: string,
	Component: any,
	elements: Element[],
	props: Record<string, any>,
): void {
	if (!Component) {
		Console.error("Missing component: ", shortcode);

		return;
	}

	elements.forEach((element) => {
		const propsJson = JSON.parse(element.getAttribute("data-props"));

		element.removeAttribute("data-props");
		element.removeAttribute("data-component");

		reactDom.hydrate(<Component {...props} {...propsJson} />, element);
	});
}
